import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Step, Icon, Segment, Header, Message, Button, Label, Divider, Progress
} from 'semantic-ui-react';
import {
  selectActivePrototype,
  selectActivePrototypeBooks,
  selectActiveLiveBooks,
  selectWizardStep,
  selectPrototypeReadiness,
  selectPrototypeLoading,
  selectPrototypeError,
  setWizardStep,
  deletePrototype,
  deleteLiveLitGroup,
  clearActivePrototype,
  setDeleteProgress,
  clearDeleteProgress,
  selectDeleteProgress,
  uploadAndAddBook,
  uploadAndAddBookToLitGroup,
  archiveLitGroupBook,
  uploadAndEditLitGroupBook,
  uploadAndSetLitGroupImage,
  removePrototypeBook,
  fetchPrototypes,
  fetchPrototypeDetail,
  publishPrototype,
  uploadAndSetPrototypeImage,
  updatePrototypeStatus,
  clearError,
} from '../../slices/litGroupPrototypeSlice';
import PrototypeDetailsForm from './PrototypeDetailsForm';
import BookEntryForm from './BookEntryForm';
import BookEntryList from './BookEntryList';
import AddSeriesBookForm from './AddSeriesBookForm';
import EditLiveBookForm from './EditLiveBookForm';
import ManageCastModal from './ManageCastModal';
import ConfirmModal from './ConfirmModal';
import FileDropZone from './FileDropZone';
import { toKebab } from './FileStagingList';
import SignedImage from './SignedImage';
import sseService from '../../services/sseService';
import { config } from '../../constants/config';
import '../../styles/landing-pages.css'; // .loading-icon — see the spinning-icon note below

// Friendly, light-touch phase labels for the import progress bar, keyed by the SSE `step`.
// Falls back to the server's raw message for any unmapped step.
const IMPORT_STEP_HINTS = {
  s3_copy: 'Preparing files…',
  pdf_conversion: 'Reading the book…',
  ai_processing: 'Analyzing dialogue…',
  story_import: 'Importing the story…',
  speech_samples: 'Creating voice samples…',
  opportunities: 'Setting up casting…',
  complete: 'Finishing up…',
};

// Progress that arrived before we knew which book it belonged to. A publish starts the pipeline
// without the client holding book uuids, so the first ticks are anonymous; the first identified
// tick takes this entry over. Kept as a distinct key so an unattributed stream still shows a bar
// rather than nothing at all.
const PENDING_IMPORT = '__pending__';

/**
 * PrototypeWizard — 4-step guided wizard for building a LitGroup prototype.
 * 
 * Restructured Flow:
 *   Step 1: LitGroup Details (PrototypeDetailsForm)
 *   Step 2: Add Books (loop) — per-book: name → PDF → cover → Save → repeat
 *   Step 3: Author Image (single file, alias defaults to author name)
 *   Step 4: Review & Ready (readiness checklist + Launch button)
 *
 * Phase 4: All file uploads go through S3 via uploadMedia, with DB persistence via Redux thunks.
 */
const PrototypeWizard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activePrototype = useSelector(selectActivePrototype);
  const books = useSelector(selectActivePrototypeBooks);
  const liveBooks = useSelector(selectActiveLiveBooks);
  const wizardStep = useSelector(selectWizardStep);
  const readiness = useSelector(selectPrototypeReadiness);
  const loading = useSelector(selectPrototypeLoading);
  const error = useSelector(selectPrototypeError);

  // Get userId from Redux store (user slice)
  const userId = useSelector((state) =>
    state.user?.profile?.data?.userId || null
  );

  // Book editing state (for the "Add Books" step)
  const [editingBook, setEditingBook] = useState(null);
  // Draft "Add a Book" state: whether the new all-in-one form (AddSeriesBookForm, with per-book
  // author name + photo) is open, overriding the Books list — mirrors the published addingToSeries.
  const [addingBook, setAddingBook] = useState(false);

  // Published-series "Add Book" state: whether the add-to-series form is open,
  // and a transient success message after a book is imported into the live LitGroup.
  // Add-to-series is a single in-place form that overrides the Books list.
  const [addingToSeries, setAddingToSeries] = useState(false);
  const [seriesAddMsg, setSeriesAddMsg] = useState(null);
  // Editing a live book's metadata (also overrides the list, in place).
  const [editingLiveBook, setEditingLiveBook] = useState(null);
  // Manage-cast (character merge/dismiss) modal for a published series.
  const [castOpen, setCastOpen] = useState(false);

  // Live add-to-series import progress. The BC needs to SEE the background import moving (~11 min)
  // instead of a dead screen. `imports` holds one entry per book in flight, fed by the per-chunk
  // PUBLISH_PROGRESS SSE.
  // The LitGroup created by a publish STARTED HERE. Held locally because the SSE listener
  // keys on it and `activePrototype.publishedLitGroupUuid` only appears after the detail
  // refetch lands — a race the progress bar would otherwise lose.
  const [publishedUuid, setPublishedUuid] = useState(null);
  const [publishing, setPublishing] = useState(false);
  // Set when the running import belongs to a PUBLISH rather than an add-to-series. The two
  // look identical to the SSE stream and read very differently to a person: one is "your
  // group is being created", the other is "one more book is joining a group you already have".
  const [publishFlow, setPublishFlow] = useState(false);
  const [publishDone, setPublishDone] = useState(false);
  // Background imports, KEYED BY BOOK. A Book Creator adding a series does not wait for one book
  // to finish before starting the next, and a single shared bar could not survive that: two
  // pipelines fed one anonymous percentage, so the bar jumped between them with nothing saying
  // which book it meant, and the first book's completion tore the bar down while the second was
  // still importing. One entry per book, each labelled, each cleared by its own completion.
  //   imports     — bookUuid -> { title, percent, step, message }  (in flight)
  //   importsDone — bookUuid -> title                              (finished this session)
  const [imports, setImports] = useState({});
  const [importsDone, setImportsDone] = useState({});
  // A delete in flight: { percent, step, message }, held in REDUX rather than here.
  //
  // This component is mounted with key={activePrototype?.uuid} — deliberately, so switching
  // Literary Groups gives a fresh instance with no stale local state. The cost is that any change
  // to the active prototype unmounts it mid-delete and takes local state with it: the bar died at
  // 5% and only came back when the next SSE tick rebuilt it, ten or twenty seconds later. Redux
  // outlives the remount.
  const deleting = useSelector(selectDeleteProgress);
  // Which confirmation is open, and everything it needs to ask its question:
  // { kind, title, body, items?, note?, confirmLabel, loading?, litGroupUuid?, books? }
  const [confirmState, setConfirmState] = useState(null);
  const importing = Object.keys(imports).length > 0;

  // Live-series imports run in the BACKGROUND (the server returns a fast 202 and streams progress
  // over SSE). Drive the progress bars from the per-chunk PUBLISH_PROGRESS ticks — each of which
  // now names its book, so concurrent imports don't share one — and finish on this LitGroup's
  // PUBLISH_COMPLETE / PUBLISH_ERROR (those carry a litGroupUuid) — refreshing the cast without a
  // manual reload.
  useEffect(() => {
    const litGroupUuid = activePrototype?.publishedLitGroupUuid || publishedUuid;
    const prototypeUuid = activePrototype?.uuid;
    if (!userId || !litGroupUuid) return undefined;

    sseService.connect(`${config.API_SSE_ENDPOINT}?userId=${encodeURIComponent(userId)}`);
    const onMessage = (msg) => {
      const d = msg && msg.data; // triggerSSEEvent wraps as { event, data, timestamp }
      if (!d) return;
      // A delete cascade runs for minutes — six books, each with characters, variations, speech
      // samples, rendered audio and two S3 roots, plus voice reclamation. Not filtered by
      // litGroupUuid: the delete is usually started from the PROTOTYPE side, where the group
      // being removed is not the one this effect is watching.
      if (d.type === 'DELETE_PROGRESS') {
        if (typeof d.percent === 'number') {
          dispatch(setDeleteProgress({ percent: d.percent, step: d.step, message: d.message || 'Deleting…' }));
        }
        return;
      }
      if (d.type === 'DELETE_COMPLETE') {
        // HOLD the bar until the refreshed list is actually on screen, then clear.
        //
        // Clearing here left a dead beat: the panel vanished the instant the server finished, and
        // the list only redrew when the refetch came back. Two seconds of a page that looks like
        // nothing happened, right after a destructive action — which is the worst moment to leave
        // someone guessing. The bar now hands straight over to the redraw.
        dispatch(setDeleteProgress({ percent: 100, step: 'complete', message: d.message || 'Finishing up…' }));
        // Redraw from the completion, not only from the HTTP response. The two normally arrive
        // together, but a cascade this long can outlive the request that started it — and a
        // delete run from another tab or another screen has no response here at all. Either way
        // the card for something that no longer exists should not be left sitting in the list.
        Promise.resolve(dispatch(fetchPrototypes()))
          .finally(() => dispatch(clearDeleteProgress()));
        if (activePrototype?.publishedLitGroupUuid === d.litGroupUuid) {
          dispatch(clearActivePrototype());
        }
        return;
      }
      // Progress ticks now carry the book they belong to (the server stamps them from the
      // pipeline's import context), so each one updates only its own bar.
      if (d.type === 'PUBLISH_PROGRESS') {
        if (d.litGroupUuid && d.litGroupUuid !== litGroupUuid) return;
        if (typeof d.percent !== 'number') return;
        const key = d.bookUuid || PENDING_IMPORT;
        setImports((prev) => {
          // A tick for a book that already completed is stale (the pipeline's tail can land after
          // the controller's completion) — don't resurrect a finished bar.
          if (!prev[key] && d.percent >= 100) return prev;
          const next = { ...prev };
          // An identified tick means the anonymous placeholder has a name now — it was only ever
          // standing in for progress that arrived before the book's uuid did.
          if (d.bookUuid && next[PENDING_IMPORT]) {
            if (!next[key]) next[key] = next[PENDING_IMPORT];
            delete next[PENDING_IMPORT];
          }
          next[key] = {
            title: d.bookTitle || next[key]?.title || null,
            percent: d.percent,
            step: d.step,
            message: d.message || 'Importing…',
          };
          return next;
        });
        return;
      }
      // Completion / failure DO carry litGroupUuid — only react to THIS group's.
      if (d.litGroupUuid !== litGroupUuid) return;
      if (d.type === 'PUBLISH_COMPLETE') {
        // An add-to-series names its book; a publish completes the whole group at once and names
        // none, which is the signal to clear every bar rather than one.
        if (d.bookUuid) {
          setImports(({ [d.bookUuid]: _gone, [PENDING_IMPORT]: _pending, ...rest }) => rest);
          setImportsDone((prev) => ({ ...prev, [d.bookUuid]: d.bookTitle || null }));
        } else {
          setImports({});
        }
        if (publishFlow) { setPublishDone(true); setPublishFlow(false); }
        setSeriesAddMsg({ type: 'success', text: d.message || `"${d.bookTitle}" finished importing.` });
        dispatch(fetchPrototypeDetail(prototypeUuid));
        dispatch(fetchPrototypes());
      } else if (d.type === 'PUBLISH_ERROR') {
        if (d.bookUuid) {
          setImports(({ [d.bookUuid]: _gone, [PENDING_IMPORT]: _pending, ...rest }) => rest);
        } else {
          setImports({});
        }
        setSeriesAddMsg({ type: 'error', text: d.message || 'Import failed.' });
      }
    };
    sseService.addEventListener('message', onMessage);
    return () => sseService.removeEventListener('message', onMessage);
  }, [userId, activePrototype?.publishedLitGroupUuid, publishedUuid, activePrototype?.uuid, publishFlow, dispatch]);

  if (!activePrototype) {
    return (
      <Segment placeholder textAlign="center">
        <Header icon>
          <Icon name="arrow left" />
          Select a Literary Group from the list or create a new one
        </Header>
      </Segment>
    );
  }

  // Where a given book's background import has got to, for the hint beside its title in the list.
  // Only knows about imports started or watched in THIS session — a reload loses the distinction
  // between "imported a minute ago" and "imported last week", which is the right amount of memory
  // for a transient status.
  const importStatusFor = (bookUuid) => {
    if (!bookUuid) return null;
    const live = imports[bookUuid];
    if (live) return { state: 'importing', percent: live.percent };
    if (bookUuid in importsDone) return { state: 'imported' };
    return null;
  };

  const isLocked = ['processing', 'complete', 'archived'].includes(activePrototype.status);
  const isSaving = loading.bookAdd || loading.bookUpdate;

  // A published LitGroup can still GROW: status 'complete' + a live LitGroup uuid.
  // Adding a book here imports it into the live LitGroup immediately (not the draft
  // prototype), and same-named characters reuse their existing voices.
  const isPublished = activePrototype.status === 'complete' && !!activePrototype.publishedLitGroupUuid;

  // Published groups display their LIVE series books; drafts display PrototypeBooks.
  const displayBooks = isPublished ? liveBooks : books;

  // Author is per-book now (set in the Add-a-Book form), so there is no group Author
  // step. Nav: Details · Books · Review.
  const steps = [
    { key: 1, icon: 'settings', title: 'Details', description: 'LitGroup info' },
    { key: 2, icon: 'book', title: 'Books', description: `${displayBooks.length} book${displayBooks.length !== 1 ? 's' : ''}` },
    { key: 3, icon: 'check circle', title: 'Review', description: 'Ready check' },
  ];

  const handleStepClick = (step) => {
    dispatch(setWizardStep(step));
    if (error) dispatch(clearError());
    // Re-fetch detail when entering Review step so S3 paths are resolved
    if (step === 3 && activePrototype?.uuid) {
      console.log('[PrototypeWizard] ▶ Dispatching fetchPrototypeDetail for uuid:', activePrototype.uuid);
      dispatch(fetchPrototypeDetail(activePrototype.uuid))
        .then((result) => {
          console.log('[PrototypeWizard] ▶ fetchPrototypeDetail result:', JSON.stringify({
            type: result?.type,
            hasPrototype: !!result?.payload?.prototype,
            authorImageS3Path: result?.payload?.prototype?.authorImageS3Path,
            booksCount: result?.payload?.books?.length,
            bookCoverPaths: result?.payload?.books?.map(b => b.coverS3Path),
          }, null, 2));
        })
        .catch((err) => {
          console.error('[PrototypeWizard] ▶ fetchPrototypeDetail ERROR:', err);
        });
    }
  };

  // Delete, and mean it.
  //
  // The old version swallowed every failure that was not a confirmation prompt: the server could
  // refuse with a perfectly clear reason and the operator saw a console line and a dialog that
  // simply closed. It now says what happened, and — where the blocker is a published Literary
  // Group still reading its credits from this prototype — offers the only thing that gets past it.
  // Two confirmations, both in-app. They ask genuinely different questions — one about a draft
  // record, one about destroying a published group and its audio — and a native confirm cannot
  // show that difference: same grey box, same OK button, and the list of books collapsed into a
  // paragraph. `confirmState` drives whichever is open.
  const handleDelete = () => {
    setSeriesAddMsg(null);
    setConfirmState({
      kind: 'prototype',
      title: `Delete “${activePrototype.name}”?`,
      body: activePrototype.status === 'complete'
        ? 'This removes the Literary Group record. If a published group was created from it, that group is checked first and this will stop rather than break it.'
        : 'This removes the draft and the files staged for it.',
      confirmLabel: 'Delete',
    });
  };

  const runPrototypeDelete = async () => {
    setConfirmState((s) => ({ ...s, loading: true }));
    try {
      const res = await dispatch(deletePrototype({
        uuid: activePrototype.uuid,
        confirmDelete: activePrototype.status === 'complete'
      })).unwrap();
      setConfirmState(null);
      setSeriesAddMsg({ type: 'success', text: res?.message || 'Deleted.' });
      dispatch(fetchPrototypes());
    } catch (err) {
      if (err?.requiresConfirmation) {
        setConfirmState({
          kind: 'prototype-force',
          title: 'One more confirmation',
          body: err.message,
          confirmLabel: 'Delete anyway',
        });
        return;
      }
      // The published group is what is really being removed, and it takes everything with it.
      // A separate question, asked separately.
      if (err?.blockedBy?.litGroupUuid) {
        setConfirmState({
          kind: 'litgroup',
          litGroupUuid: err.blockedBy.litGroupUuid,
          books: err.blockedBy.books || [],
          title: `Delete the Literary Group “${activePrototype.name}” and everything in it?`,
          body: err.message,
          note: 'This removes those books, their characters and voice assignments, any audio '
            + 'already rendered, and the stored files. It cannot be undone.',
          confirmLabel: 'Delete the group',
        });
        return;
      }
      setConfirmState(null);
      setSeriesAddMsg({ type: 'error', text: err?.message || (typeof err === 'string' ? err : 'Delete failed.') });
    }
  };

  // Force-delete the prototype after the server asked for a second confirmation.
  const runPrototypeForceDelete = async () => {
    setConfirmState((s) => ({ ...s, loading: true }));
    try {
      await dispatch(deletePrototype({ uuid: activePrototype.uuid, confirmDelete: true })).unwrap();
      setSeriesAddMsg({ type: 'success', text: 'Deleted.' });
      dispatch(fetchPrototypes());
    } catch (e) {
      setSeriesAddMsg({ type: 'error', text: e?.message || (typeof e === 'string' ? e : 'Delete failed.') });
    } finally {
      setConfirmState(null);
    }
  };

  // The published group takes everything with it — the books, their characters and voices, any
  // rendered audio, the S3 files, and finally the prototype that was blocking. None of it is
  // recoverable, which is why it is its own question rather than folded into the first one.
  const runLitGroupDelete = async () => {
    const { litGroupUuid, books = [] } = confirmState || {};
    setConfirmState(null);
    // Shown from the click, not from the first SSE tick — the server has to find the owner and
    // collect the voice ids before it can say anything, and a few seconds of nothing after
    // confirming a destructive action reads as a dead button.
    dispatch(setDeleteProgress({ percent: 1, message: 'Starting…' }));
    try {
      const res = await dispatch(deleteLiveLitGroup({ litGroupUuid })).unwrap();
      setSeriesAddMsg({
        type: 'success',
        text: `"${activePrototype.name}" and its ${books.length} book${books.length === 1 ? '' : 's'} were deleted.`
          + (res?.warnings?.length ? ` (${res.warnings.join('; ')})` : ''),
      });
      // The bar is NOT cleared here. It is held until the refreshed list lands — see
      // DELETE_COMPLETE — so there is no beat where the panel has gone and the stale card is
      // still sitting there. The refetch below is what finally clears it.
      Promise.resolve(dispatch(fetchPrototypes()))
        .finally(() => dispatch(clearDeleteProgress()));
      // The wizard is now showing a group that no longer exists. Hold it long enough for the
      // confirmation to be read, then step back to the list — clearing immediately would take
      // the message with it, since the wizard renders a placeholder with nothing selected.
      setTimeout(() => dispatch(clearActivePrototype()), 2500);
    } catch (delErr) {
      setSeriesAddMsg({
        type: 'error',
        text: typeof delErr === 'string' ? delErr : (delErr?.message || 'Could not delete the Literary Group.'),
      });
      // Only on failure. A request that dies before the server gets going never sends
      // DELETE_COMPLETE, so nothing else would take the bar down.
      dispatch(clearDeleteProgress());
    }
  };

  // ==========================================
  // Handle saving a book: S3 upload + DB persist
  // ==========================================
  const handleSaveBook = async (bookData) => {
    console.log('[PrototypeWizard] Save book:', bookData);

    try {
      await dispatch(uploadAndAddBook({
        prototypeUuid: activePrototype.uuid,
        bookTitle: bookData.bookTitle,
        pdfFile: bookData.pdfFile,
        pdfAlias: bookData.pdfAlias,
        coverFile: bookData.coverFile,
        coverAlias: bookData.coverAlias || null,
        userId,
      })).unwrap();

      console.log('[PrototypeWizard] Book saved successfully');
    } catch (err) {
      console.error('[PrototypeWizard] Error saving book:', err);
      // Error is set in Redux state via the thunk's rejected handler
    }

    setEditingBook(null);
  };

  // Draft "Add a Book" (new all-in-one form). Mirrors handleAddSeriesBook but targets the draft
  // prototype via uploadAndAddBook. The previous draft book seeds this book's author; the very
  // first photo picked becomes the prototype's author-image canonical (version 1).
  const lastDraftBook = books && books.length ? books[books.length - 1] : null;
  const seedDraftAuthorName = lastDraftBook?.authorName || activePrototype.authorName || '';
  const seedDraftAuthorImageS3Path = lastDraftBook?.authorImageS3Path || activePrototype.authorImageS3Path || null;

  const handleAddDraftBook = async (data) => {
    const maxVersion = activePrototype.authorImageMaxVersion || activePrototype.authorImageVersion || 1;
    // Pin a version only when an image actually exists (a new upload, or an existing canonical to
    // inherit). No canonical yet + no photo picked → leave unpinned (null) so no broken S3 path.
    let authorImageVersion = activePrototype.authorImageFileName ? (activePrototype.authorImageVersion || 1) : null;
    let newAuthorImageFile = null;
    let authorImageFileName = null;
    if (data.newAuthorFile) {
      // No canonical image yet → this is version 1 (the canonical); otherwise allocate the next.
      authorImageVersion = activePrototype.authorImageFileName ? (maxVersion + 1) : 1;
      newAuthorImageFile = data.newAuthorFile;
      const ext = '.' + (data.newAuthorFile.name.split('.').pop() || 'jpg');
      authorImageFileName = activePrototype.authorImageFileName
        || (toKebab(data.authorName || activePrototype.authorName || 'author') + ext);
    }
    try {
      await dispatch(uploadAndAddBook({
        prototypeUuid: activePrototype.uuid,
        bookTitle: data.bookTitle,
        pdfFile: data.pdfFile,
        pdfAlias: data.pdfAlias,
        coverFile: data.coverFile,
        coverAlias: data.coverAlias || null,
        userId,
        authorName: data.authorName,
        newAuthorImageFile,
        authorImageVersion,
        authorImageFileName,
        authorUuid: activePrototype.authorUuid,
      })).unwrap();
      setAddingBook(false);
      dispatch(fetchPrototypeDetail(activePrototype.uuid));
    } catch (err) {
      console.error('[PrototypeWizard] Error adding draft book:', err);
      // Error surfaced via Redux state; the form stays open for retry.
    }
  };

  // ==========================================
  // Handle adding a book to an ALREADY-PUBLISHED LitGroup (series grows over time)
  // Uploads to the source prototype's S3 path, then imports into the live LitGroup.
  // ==========================================
  // Close the add-to-series form (return to the list).
  const resetAddSeries = () => setAddingToSeries(false);

  // The previous book seeds this book's author (name + photo); fall back to the
  // series/prototype author for the very first added book.
  const lastLiveBook = liveBooks && liveBooks.length ? liveBooks[liveBooks.length - 1] : null;
  const seedAuthorName = lastLiveBook?.authorName || activePrototype.authorName || '';
  const seedAuthorImageS3Path = lastLiveBook?.authorImageS3Path || activePrototype.authorImageS3Path || null;
  const seedAuthorVersion = lastLiveBook?.authorImageVersion || activePrototype.authorImageVersion || 1;

  // Single terminal action: import the fully-entered book into the live LitGroup.
  const handleAddSeriesBook = async (data) => {
    setSeriesAddMsg(null);

    // Author image: pin to the seed (previous book's) version unless a NEW photo was
    // staged, in which case allocate the next version from the max counter. Filename
    // stays STABLE across versions, so older books keep their photo.
    const maxVersion = activePrototype.authorImageMaxVersion || activePrototype.authorImageVersion || 1;
    let authorImageVersion = seedAuthorVersion;
    let newAuthorImageFile = null;
    let authorImageFileName = null;
    if (data.newAuthorFile) {
      authorImageVersion = maxVersion + 1;
      newAuthorImageFile = data.newAuthorFile;
      const ext = '.' + (data.newAuthorFile.name.split('.').pop() || 'jpg');
      authorImageFileName = activePrototype.authorImageFileName
        || (toKebab(data.authorName || activePrototype.authorName || 'author') + ext);
    }

    try {
      const added = await dispatch(uploadAndAddBookToLitGroup({
        litGroupUuid: activePrototype.publishedLitGroupUuid,
        prototypeUuid: activePrototype.uuid, // == LitGroup.sourcePrototypeUuid (S3 source path)
        bookTitle: data.bookTitle,
        pdfFile: data.pdfFile,
        pdfAlias: data.pdfAlias,
        coverFile: data.coverFile,
        coverAlias: data.coverAlias || null,
        userId,
        authorName: data.authorName,
        authorImageVersion,
        newAuthorImageFile,
        authorImageFileName,
        authorUuid: activePrototype.authorUuid,
      })).unwrap();

      resetAddSeries();
      // Show the live progress bar — the import runs in the background (~11 min) and updates via
      // SSE. Clear the transient message; the bar is the indicator now.
      setSeriesAddMsg(null);
      // The 202 names the book, so its bar is labelled from the first frame rather than waiting
      // for a tick to say who it is — which matters most when a second book joins a first
      // already in flight.
      const key = added?.bookUuid || PENDING_IMPORT;
      setImports((prev) => ({
        ...prev,
        [key]: { title: added?.bookTitle || data.bookTitle, percent: 2, message: 'Starting import…' },
      }));
      // Refresh the list + the card's book count without a page reload (the new book shows now;
      // its cast fills in when the background import finishes).
      dispatch(fetchPrototypeDetail(activePrototype.uuid));
      dispatch(fetchPrototypes());
    } catch (err) {
      console.error('[PrototypeWizard] Error adding book to LitGroup:', err);
      // Error surfaced via Redux state; the form stays open for retry.
    }
  };

  // Save metadata edits to a live book (title / cover / author name+photo). No re-import.
  const handleEditLiveBook = async (data) => {
    if (!editingLiveBook) return;
    setSeriesAddMsg(null);

    // Author photo: if replaced, allocate the next version (canonical never moves).
    const maxVersion = activePrototype.authorImageMaxVersion || activePrototype.authorImageVersion || 1;
    let authorImageVersion = editingLiveBook.authorImageVersion || activePrototype.authorImageVersion || 1;
    let authorImageFileName = null;
    if (data.newAuthorFile) {
      authorImageVersion = maxVersion + 1;
      const ext = '.' + (data.newAuthorFile.name.split('.').pop() || 'jpg');
      authorImageFileName = activePrototype.authorImageFileName
        || (toKebab(data.authorName || activePrototype.authorName || 'author') + ext);
    }

    try {
      await dispatch(uploadAndEditLitGroupBook({
        litGroupUuid: activePrototype.publishedLitGroupUuid,
        prototypeUuid: activePrototype.uuid,
        bookUuid: editingLiveBook.uuid,
        bookTitle: data.bookTitle,
        authorName: data.authorName,
        coverFile: data.coverFile,
        coverAlias: data.coverAlias,
        newAuthorFile: data.newAuthorFile,
        authorImageVersion,
        authorImageFileName,
        authorUuid: activePrototype.authorUuid,
        userId,
        // Audiobook credits, forwarded untouched. Absent means "leave alone" server-side and
        // empty means "clear", so this must pass through rather than be defaulted here.
        credits: data.credits,
        audioCoverFile: data.audioCoverFile,
        audioCoverAlias: data.audioCoverAlias,
      })).unwrap();

      setEditingLiveBook(null);
      setSeriesAddMsg({ type: 'success', text: `"${data.bookTitle}" updated.` });
      dispatch(fetchPrototypeDetail(activePrototype.uuid));
    } catch (err) {
      console.error('[PrototypeWizard] Error editing live book:', err);
    }
  };

  // ==========================================
  // Handle archiving (soft-delete) a live series book.
  // Preserves shared characters/voices/opportunities; reversible.
  // ==========================================
  /**
   * Publish THIS prototype, here, without leaving the wizard.
   *
   * The server stages the LitGroup and Book nodes and returns 202; the per-book import then
   * runs in the background and reports over SSE — the same events this component already
   * listens to for adding a book to a live series, so the progress bar is reused rather than
   * rebuilt.
   */
  /**
   * The group's picture, whichever state the group is in.
   *
   * Published → straight to the live LitGroup. Draft → staged under the prototype, and
   * copied across at publish (prototypePublishService.copyGroupImage). The operator does not
   * need to know which of those happened; they are choosing a picture for their group.
   */
  const handleGroupImage = async (files) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file || !activePrototype) return;
    try {
      if (isPublished && activePrototype.publishedLitGroupUuid) {
        await dispatch(uploadAndSetLitGroupImage({
          litGroupUuid: activePrototype.publishedLitGroupUuid, imageFile: file, userId,
        })).unwrap();
      } else {
        await dispatch(uploadAndSetPrototypeImage({
          prototypeUuid: activePrototype.uuid, imageFile: file, userId,
        })).unwrap();
      }
      dispatch(fetchPrototypeDetail(activePrototype.uuid));
    } catch (err) {
      console.error('[PrototypeWizard] group image upload failed:', err);
    }
  };

  const handlePublish = async () => {
    setSeriesAddMsg(null);
    setPublishing(true);
    try {
      const res = await dispatch(publishPrototype({ prototypeUuid: activePrototype.uuid })).unwrap();
      if (res?.litGroupUuid) setPublishedUuid(res.litGroupUuid);
      // The import is NOT done — 202 means staged. Hand over to the same progress path a
      // series-add uses; PUBLISH_COMPLETE clears it.
      setPublishFlow(true);
      setPublishDone(false);
      setImports({ [PENDING_IMPORT]: { title: null, percent: 5, message: 'Importing books…' } });
      dispatch(fetchPrototypeDetail(activePrototype.uuid));
      dispatch(fetchPrototypes());
    } catch (err) {
      console.error('[PrototypeWizard] publish failed:', err);
      setSeriesAddMsg({ type: 'error', text: typeof err === 'string' ? err : (err?.message || 'Publish failed.') });
    } finally {
      setPublishing(false);
    }
  };

  const handleArchiveLiveBook = async (book) => {
    try {
      await dispatch(archiveLitGroupBook({
        litGroupUuid: activePrototype.publishedLitGroupUuid,
        bookUuid: book.uuid,
      })).unwrap();
      setSeriesAddMsg({
        type: 'success',
        text: `"${book.bookTitle}" was removed from the series. Its shared characters, voices, and any assigned opportunities were preserved.`,
      });
      // Keep the card list's book count in sync without a page reload.
      dispatch(fetchPrototypes());
    } catch (err) {
      console.error('[PrototypeWizard] Error archiving book:', err);
    }
  };

  // ==========================================
  // Handle the LitGroup's OWN profile image (published groups; distinct from author image)
  // ==========================================
  const handleLitGroupImageUpload = async (files) => {
    const file = files && files[0];
    if (!file || !isPublished) return;
    try {
      await dispatch(uploadAndSetLitGroupImage({
        litGroupUuid: activePrototype.publishedLitGroupUuid,
        imageFile: file,
        userId,
      })).unwrap();
    } catch (err) {
      console.error('[PrototypeWizard] Error setting LitGroup image:', err);
    }
  };

  // ==========================================
  // Handle editing a book
  // ==========================================
  const handleEditBook = (book) => {
    setEditingBook(book);
  };

  // ==========================================
  // Handle removing a book: DB delete
  // ==========================================
  const handleRemoveBook = async (book) => {
    if (!book.uuid) {
      console.warn('[PrototypeWizard] Cannot remove book without uuid');
      return;
    }

    try {
      await dispatch(removePrototypeBook({
        prototypeUuid: activePrototype.uuid,
        bookUuid: book.uuid,
      })).unwrap();
      console.log('[PrototypeWizard] Book removed:', book.uuid);
    } catch (err) {
      console.error('[PrototypeWizard] Error removing book:', err);
    }

    if (editingBook?.uuid === book.uuid) {
      setEditingBook(null);
    }
  };

  // Render the content for the active wizard step
  const renderStepContent = () => {
    switch (wizardStep) {
      // ====================================
      // STEP 1: LitGroup Details
      // ====================================
      case 1:
        return (
          <div>
            {/* The LitGroup's OWN profile image — a visual identity distinct from the author
                image. Offered from the START now: a draft stages it under the prototype and
                publish copies it across, the same two-step the author image already uses.
                Previously this appeared only once the group was live, so the one moment an
                operator is thinking about the group's identity — creating it — was the one
                moment they could not give it a picture. */}
            {(
              <Segment>
                <Header as="h4">
                  <Icon name="images" />
                  <Header.Content>
                    Literary Group Profile Image
                    <Header.Subheader>The group's own picture — distinct from the author photo</Header.Subheader>
                  </Header.Content>
                </Header>
                <div style={{ display: 'flex', gap: '1.5em', alignItems: 'center' }}>
                  <SignedImage
                    s3Path={activePrototype.litGroupImageUrl || activePrototype.groupImageS3Path}
                    userId={userId}
                    alt={activePrototype.name || 'Literary Group'}
                    size="medium"
                    width="90px"
                    height="90px"
                    fallbackIcon="book"
                  />
                  <div style={{ flex: 1 }}>
                    <FileDropZone
                      accept=".jpg,.jpeg,.png,.webp"
                      multiple={false}
                      label={activePrototype.litGroupImageUrl ? 'Replace the group image' : 'Upload a group profile image'}
                      sublabel="Shown as the Literary Group's visual identity"
                      icon="images"
                      maxSizeMB={10}
                      disabled={loading.litGroupImage}
                      onFilesSelected={handleGroupImage}
                    />
                    {loading.litGroupImage && (
                      <Message info size="mini" style={{ marginTop: '0.5em' }}>
                        <Icon className="loading-icon" name="spinner" loading /> Uploading group image…
                      </Message>
                    )}
                  </div>
                </div>
              </Segment>
            )}
            <PrototypeDetailsForm mode="edit" />
          </div>
        );

      // ====================================
      // STEP 2: Add Books (loop)
      // ====================================
      case 2:
        return (
          <div>
            {/* Error display */}
            {error && (
              <Message negative size="small" onDismiss={() => dispatch(clearError())}>
                <Icon name="warning sign" />
                {typeof error === 'string' ? error : error.message || 'An error occurred'}
              </Message>
            )}

            {/* PUBLISHED series → live books List ⇄ all-in-one Add form (form OVERRIDES the
                list, so long series aren't pushed down). DRAFT → PrototypeBooks + entry form. */}
            {isPublished ? (
              editingLiveBook ? (
                <EditLiveBookForm
                  book={editingLiveBook}
                  onSubmit={handleEditLiveBook}
                  onCancel={() => setEditingLiveBook(null)}
                  saving={loading.litGroupBookEdit}
                  userId={userId}
                />
              ) : addingToSeries ? (
                <AddSeriesBookForm
                  onSubmit={handleAddSeriesBook}
                  onCancel={resetAddSeries}
                  saving={loading.litGroupBookAdd}
                  defaultAuthorName={seedAuthorName}
                  currentAuthorImageS3Path={seedAuthorImageS3Path}
                  userId={userId}
                  submitLabel="Add Book to Series"
                />
              ) : (
                <>
                  {seriesAddMsg && (
                    <Message
                      positive={seriesAddMsg.type === 'success'}
                      negative={seriesAddMsg.type === 'error'}
                      onDismiss={() => setSeriesAddMsg(null)}
                      style={{ marginBottom: '1em' }}
                    >
                      <Icon name={seriesAddMsg.type === 'success' ? 'check circle' : 'warning sign'} />
                      {seriesAddMsg.text}
                    </Message>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5em' }}>
                    <Button basic size="small" icon labelPosition="left"
                      onClick={() => setCastOpen(true)}>
                      <Icon name="users" />
                      Manage Cast
                    </Button>
                  </div>
                  <BookEntryList
                    books={liveBooks}
                    live
                    userId={userId}
                    importStatus={importStatusFor}
                    onEditBook={(b) => { setSeriesAddMsg(null); setEditingLiveBook(b); }}
                    onRemoveBook={handleArchiveLiveBook}
                    disabled={loading.litGroupBookArchive}
                    removeConfirmMessage={(b) => `Remove "${b.bookTitle}" from the series?\n\nThe book is hidden from the group. Its characters, voices, and any assigned opportunities are preserved (they are shared across the series). To fix a wrong upload, remove it here and then use "Add Book to Series".`}
                    onAddBook={() => { setSeriesAddMsg(null); setAddingToSeries(true); }}
                    addLabel="Add Book to Series"
                  />
                </>
              )
            ) : (
              editingBook ? (
                // Edit an existing draft book — keeps the original per-book editor.
                <BookEntryForm
                  onSaveBook={handleSaveBook}
                  editingBook={editingBook}
                  onCancelEdit={() => setEditingBook(null)}
                  disabled={isLocked}
                  saving={isSaving}
                />
              ) : addingBook ? (
                // Add a book — the all-in-one form with per-book author name + photo.
                <AddSeriesBookForm
                  onSubmit={handleAddDraftBook}
                  onCancel={() => setAddingBook(false)}
                  saving={isSaving}
                  defaultAuthorName={seedDraftAuthorName}
                  currentAuthorImageS3Path={seedDraftAuthorImageS3Path}
                  userId={userId}
                  submitLabel="Save Book"
                />
              ) : (
                <>
                  <BookEntryList
                    books={books}
                    onEditBook={handleEditBook}
                    onRemoveBook={handleRemoveBook}
                    disabled={isLocked || loading.bookRemove}
                    onAddBook={!isLocked ? () => setAddingBook(true) : undefined}
                    addLabel="Add a Book"
                  />

                  {/* BookEntryList renders nothing when empty — show a first-book prompt so a
                      brand-new LitGroup isn't a blank panel. */}
                  {(!books || books.length === 0) && !isLocked && (
                    <Segment placeholder textAlign="center">
                      <Header icon>
                        <Icon name="book" />
                        No books yet
                      </Header>
                      <Button primary icon labelPosition="left" onClick={() => setAddingBook(true)}>
                        <Icon name="plus" /> Add a Book
                      </Button>
                    </Segment>
                  )}

                  {isLocked && (!books || books.length === 0) && (
                    <Message warning>
                      <Icon name="lock" />
                      This Literary Group is locked. No books can be added.
                    </Message>
                  )}
                </>
              )
            )}
          </div>
        );

      // ====================================
      // STEP 3: Review & Ready
      // ====================================
      case 3:
        // DIAGNOSTIC: Log all S3 path data so we can see what's available
        console.log('[Step4 DIAG] activePrototype:', JSON.stringify({
          uuid: activePrototype.uuid,
          name: activePrototype.name,
          authorImageFileName: activePrototype.authorImageFileName,
          authorImageS3Path: activePrototype.authorImageS3Path,
          authorImageVersion: activePrototype.authorImageVersion,
          authorUuid: activePrototype.authorUuid,
        }, null, 2));
        console.log('[Step4 DIAG] userId:', userId);
        console.log('[Step4 DIAG] books:', books.map(b => ({
          uuid: b.uuid,
          bookTitle: b.bookTitle,
          fileName: b.fileName,
          coverFileName: b.coverFileName,
          coverS3Path: b.coverS3Path,
          pdfS3Path: b.pdfS3Path,
        })));
        return (
          <div>
            {/* Summary */}
            <Segment>
              <Header as="h3">
                <Icon name="clipboard check" />
                <Header.Content>
                  Literary Group Summary
                  <Header.Subheader>Review your Literary Group before launching automation</Header.Subheader>
                </Header.Content>
              </Header>

              <div style={{ fontSize: '1.05em', lineHeight: '2em' }}>
                {/* Group Image + Details + Author Image row */}
                <div style={{ display: 'flex', gap: '1.5em', alignItems: 'flex-start', marginBottom: '0.5em' }}>
                  {/* Literary Group profile image (left side) — the group's own identity */}
                  {isPublished && (
                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                      <div style={{ fontSize: '0.9rem', color: '#221f1f', marginBottom: '0.3em' }}>
                        Literary Group Image
                      </div>
                      <SignedImage
                        s3Path={activePrototype.litGroupImageUrl}
                        userId={userId}
                        alt={activePrototype.name || 'Literary Group'}
                        size="small"
                        width='90px'
                        height='90px'
                        fallbackIcon="book"
                      />
                    </div>
                  )}

                  {/* Details */}
                  <div style={{ flexShrink: 0 }}>
                    <div><strong>Name:</strong> {activePrototype.name}</div>
                    {activePrototype.genre && <div><strong>Genre:</strong> {activePrototype.genre}</div>}
                    {activePrototype.litGroupType && <div><strong>Type:</strong> {activePrototype.litGroupType}</div>}
                    {activePrototype.authorName && <div><strong>Author:</strong> {activePrototype.authorName}</div>}
                    {activePrototype.description && (
                      <div style={{ marginTop: '0.3em' }}>
                        <strong>Description:</strong> {activePrototype.description}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontSize: '0.9rem', color: '#221f1f', marginTop: '0.3em' }}>
                      Author Image
                    </div>
                    <SignedImage
                      s3Path={activePrototype.authorImageS3Path}
                      userId={userId}
                      alt={activePrototype.authorName || 'Author'}
                      size="small"
                      rounded
                      width='70px'
                      height='70px'
                      fallbackIcon="user circle"
                    />
                    <div style={{ fontSize: '0.8em', color: '#888', marginTop: '-0.8rem' }}>
                      {activePrototype.authorImageFileName
                        ? activePrototype.authorImageFileName
                        : 'No image'}
                    </div>
                  </div>
                </div>

                <Divider />

                {/* Books grid with cover thumbnails (live series when published, else prototype books) */}
                <div><strong>Books:</strong> {displayBooks.length}</div>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '1em',
                  marginTop: '0.5em',
                  padding: '0.5em 0',
                }}>
                  {displayBooks.map((book, i) => {
                    const hasCover = !!(book.coverFileName || book.coverUrl);
                    return (
                      <div
                        key={book.uuid || i}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: 110,
                          textAlign: 'center',
                        }}
                      >
                        <SignedImage
                          s3Path={book.coverS3Path || book.coverUrl}
                          userId={userId}
                          alt={book.bookTitle || book.fileName}
                          width='60px'
                          size="medium"
                          fallbackIcon="book"
                        />
                        <div style={{
                          fontSize: '0.8em',
                          marginTop: '0.3em',
                          lineHeight: '1.3em',
                          maxWidth: 110,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {book.bookTitle || book.fileName}
                        </div>
                        {book.live && book.addedToExistingLitGroup && (
                          <Label size="mini" color="teal" style={{ marginTop: '0.2em' }}>added</Label>
                        )}
                        {hasCover ? (
                          <Label size="mini" color="green" style={{ marginTop: '0.2em' }}>
                            <Icon name="check" style={{ margin: 0 }} />
                          </Label>
                        ) : (
                          <Label size="mini" color="yellow" style={{ marginTop: '0.2em' }}>
                            no cover
                          </Label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Segment>

            {/* Readiness checklist */}
            <Segment color={readiness.ready ? 'green' : 'yellow'}>
              <Header as="h4">
                <Icon name={readiness.ready ? 'check circle' : 'tasks'} color={readiness.ready ? 'green' : 'yellow'} />
                <Header.Content>
                  {readiness.ready ? 'Ready for Automation!' : 'Readiness Checklist'}
                </Header.Content>
              </Header>
              {/* REQUIRED vs OPTIONAL, stated rather than implied.
                  Only two things gate readiness — `ready = detailsComplete && hasPdfs` — but
                  all four items rendered identically, as unchecked circles in a yellow panel.
                  Someone who has not done this before sees four incomplete rows and no way to
                  tell which two are stopping them, and the blocking message said "complete the
                  checklist above", which implies all of them. */}
              <div style={{ fontSize: '0.95em' }}>
                <div style={{ fontWeight: 'bold', fontSize: '0.85em', color: '#767676', marginBottom: '0.2em' }}>
                  REQUIRED
                </div>
                <div>
                  <Icon name={readiness.checks.details ? 'check circle' : 'circle outline'} color={readiness.checks.details ? 'green' : 'grey'} />
                  A name for the Literary Group
                </div>
                <div>
                  <Icon name={readiness.checks.hasPdfs ? 'check circle' : 'circle outline'} color={readiness.checks.hasPdfs ? 'green' : 'grey'} />
                  At least one book ({books.length} added)
                </div>

                <div style={{ fontWeight: 'bold', fontSize: '0.85em', color: '#767676', margin: '0.7em 0 0.2em' }}>
                  OPTIONAL — these do not hold up publishing
                </div>
                <div style={{ color: '#767676' }}>
                  <Icon name={readiness.checks.allCoversMatched ? 'check circle' : 'circle outline'} color={readiness.checks.allCoversMatched ? 'green' : 'grey'} />
                  Book covers ({readiness.checks.coverMatchRatio})
                </div>
                <div style={{ color: '#767676' }}>
                  <Icon name={readiness.checks.authorImage ? 'check circle' : 'circle outline'} color={readiness.checks.authorImage ? 'green' : 'grey'} />
                  Author image {!readiness.checks.authorImage && '— a default is used without one'}
                </div>
              </div>

              {/* Status Action Buttons */}
              {!isLocked && activePrototype.status === 'draft' && readiness.ready && (
                <Button
                  color="green"
                  size="large"
                  style={{ marginTop: '1em' }}
                  loading={loading.status}
                  onClick={async () => {
                    try {
                      await dispatch(updatePrototypeStatus({
                        uuid: activePrototype.uuid,
                        status: 'ready'
                      })).unwrap();
                      console.log('[PrototypeWizard] ✅ Status changed to ready');
                    } catch (err) {
                      console.error('[PrototypeWizard] Error marking as ready:', err);
                    }
                  }}
                >
                  <Icon name="check circle" />
                  Mark as Ready
                </Button>
              )}

              {!isLocked && activePrototype.status === 'draft' && !readiness.ready && (
                <Message info size="small" style={{ marginTop: '1em' }}>
                  <Icon name="info circle" />
                  {' '}Still needed:{' '}
                  {[
                    !readiness.checks.details && 'a name for the Literary Group (Details step)',
                    !readiness.checks.hasPdfs && 'at least one book (Books step)',
                  ].filter(Boolean).join(', and ')}.
                </Message>
              )}

              {activePrototype.status === 'ready' && (
                <div style={{ marginTop: '1em' }}>
                  {/* Publishes THIS Literary Group, in place.
                      It used to link to /publish-prototypes — a queue built to publish MANY
                      groups at once, which this product does not do. Everything that went
                      wrong came from the detour rather than from publishing: the page had to
                      LIST prototypes, so every ready draft ever abandoned appeared beside
                      this one; it had a QUEUE, so it offered a Cancel it could not honour;
                      and it was a separate PAGE, so Back left the wizard entirely. None of
                      that is fixed — it is simply no longer in the way. */}
                  <Button
                    primary
                    size="large"
                    loading={publishing}
                    disabled={publishing || importing}
                    onClick={handlePublish}
                  >
                    <Icon name="rocket" />
                    Publish this Literary Group
                  </Button>
                  <Button
                    basic
                    size="small"
                    style={{ marginLeft: '0.5em' }}
                    loading={loading.status}
                    onClick={async () => {
                      try {
                        await dispatch(updatePrototypeStatus({
                          uuid: activePrototype.uuid,
                          status: 'draft'
                        })).unwrap();
                        console.log('[PrototypeWizard] ✅ Status reverted to draft');
                      } catch (err) {
                        console.error('[PrototypeWizard] Error reverting to draft:', err);
                      }
                    }}
                  >
                    <Icon name="undo" />
                    Back to Draft
                  </Button>
                </div>
              )}
            </Segment>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {/* Prototype header bar */}
      <Segment style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
          {isPublished && (
            <SignedImage
              s3Path={activePrototype.litGroupImageUrl}
              userId={userId}
              alt={activePrototype.name || 'Literary Group'}
              size="small"
              width="48px"
              height="48px"
              rounded
              fallbackIcon="book"
            />
          )}
          <div>
            <Header as="h2" style={{ margin: 0 }}>
              {activePrototype.name}
              {isLocked && (
                <Label color="grey" size="small" style={{ marginLeft: '0.5em' }}>
                  <Icon name="lock" /> Read-only
                </Label>
              )}
            </Header>
            {activePrototype.authorName && (
              <span style={{ color: '#666' }}>by {activePrototype.authorName}</span>
            )}
          </div>
        </div>
        <div>
          <Label color={activePrototype.status === 'ready' ? 'green' : activePrototype.status === 'complete' ? 'teal' : 'yellow'}>
            {activePrototype.status?.toUpperCase()}
          </Label>
          {/* Delete stays available for a PUBLISHED ('complete') prototype — only hidden mid-import
              ('processing'). isLocked (which also covers 'complete') governs read-only EDITING, not delete;
              handleDelete already sends confirmDelete for a complete prototype. */}
          {activePrototype.status !== 'processing' && (
            <Button
              icon="trash"
              color="red"
              size="mini"
              basic
              onClick={handleDelete}
              loading={loading.delete}
              style={{ marginLeft: '0.5em' }}
              title="Delete Literary Group prototype"
            />
          )}
        </div>
      </Segment>

      {/* Step navigation */}
      <Step.Group fluid size="small" widths={4}>
        {steps.map(step => (
          <Step
            key={step.key}
            active={wizardStep === step.key}
            onClick={() => handleStepClick(step.key)}
            style={{ cursor: 'pointer' }}
          >
            <Icon name={step.icon} />
            <Step.Content>
              <Step.Title>{step.title}</Step.Title>
              <Step.Description>{step.description}</Step.Description>
            </Step.Content>
          </Step>
        ))}
      </Step.Group>

      {/* Delete confirmations, ours rather than the browser's. The second one lists the books by
          name, which is the whole reason it is a modal: in a native confirm that list arrives as
          bullet characters run together in a paragraph. */}
      <ConfirmModal
        open={!!confirmState}
        title={confirmState?.title}
        items={confirmState?.books}
        note={confirmState?.note}
        confirmLabel={confirmState?.confirmLabel}
        loading={!!confirmState?.loading}
        onCancel={() => setConfirmState(null)}
        onConfirm={() => {
          if (confirmState?.kind === 'litgroup') return runLitGroupDelete();
          if (confirmState?.kind === 'prototype-force') return runPrototypeForceDelete();
          return runPrototypeDelete();
        }}
      >
        {confirmState?.body}
      </ConfirmModal>

      {/* Deletion progress. A LitGroup cascade is minutes of work — books, characters,
          variations, speech samples, rendered audio, two S3 roots per book, ElevenLabs voice
          reclamation, then the source prototype. Red rather than the import's neutral grey:
          this one is not reversible, and it should not be mistaken at a glance for an import.

          .loading-icon on the spinner: Semantic's `loading` icons carry their own box metrics, so
          left alone they sit off the text baseline and wobble as they turn. The class pins them
          to 1rem square with no margin and centres them vertically. Every spinning icon in this
          file uses it. */}
      {deleting && (
        <Segment color="red" style={{ marginTop: '1em' }}>
          <div style={{ marginBottom: '0.5em' }}>
            <Icon className="loading-icon" name="trash alternate" loading />{' '}
            {deleting.message || 'Deleting…'}
          </div>
          <Progress
            percent={deleting.percent}
            indicating
            progress
            size="small"
            color="red"
            style={{ margin: 0 }}
          />
          <div style={{ fontSize: '0.85em', fontWeight: 300, color: '#999', marginTop: '0.3em' }}>
            Removing books, characters, voices, rendered audio and stored files. Please leave this
            page open until it finishes.
          </div>
        </Segment>
      )}

      {/* Live import progress — visible on any step while a background add-to-series runs, so the
          Book Creator can see it's working (and safely step away). */}
      {importing && (
        <Segment style={{ marginTop: '1em' }}>
          <div style={{ marginBottom: '0.5em' }}>
            <Icon className="loading-icon" name="cog" loading />{' '}
            {publishFlow
              ? `Creating "${activePrototype.name}" and importing its books — this runs on its own, so you can leave this page and come back. It'll update here when it's done.`
              : `Importing ${Object.keys(imports).length > 1 ? `${Object.keys(imports).length} books` : 'your book'} — this runs on its own, so you can leave this page and come back. It'll update here when it's done.`}
          </div>
          {/* One bar per book in flight, each with its title above it. With a single shared bar
              there was no way to tell whose percentage was showing once a second book was
              started — and the second book's bar vanished the moment the first one finished. */}
          {Object.entries(imports).map(([key, imp], i) => (
            <div key={key} style={{ marginTop: i === 0 ? 0 : '0.9em' }}>
              {imp.title && (
                <div style={{ fontSize: '0.9em', fontWeight: 600, marginBottom: '0.2em' }}>
                  {imp.title}
                </div>
              )}
              <Progress percent={imp.percent} indicating progress size="small" style={{ margin: 0 }} />
              {(IMPORT_STEP_HINTS[imp.step] || imp.message) && (
                <div style={{ fontSize: '0.85em', fontWeight: 300, color: '#999', marginTop: '0.3em' }}>
                  {IMPORT_STEP_HINTS[imp.step] || imp.message}
                </div>
              )}
            </div>
          ))}
        </Segment>
      )}

      {/* THE END OF THE FLOW.
          Create → add books → publish → import finishes … and then nothing said what to do
          next. An experienced operator knows the answer is "go and cast the voices"; someone
          doing this for the first time is left on a Review screen with no action on it,
          unsure whether anything more is required of them.
          Shown only for a publish, and only once — an add-to-series ends where it started. */}
      {publishDone && (
        <Message positive style={{ marginTop: '1em' }}>
          <Message.Header>
            <Icon name="check circle" /> “{activePrototype.name}” is live
          </Message.Header>
          <p style={{ marginTop: '0.5em' }}>
            Its books are imported and its characters are derived. The next step is voices:
            derive casting, merge any duplicate characters, then cast and render.
          </p>
          <Button
            primary
            size="small"
            onClick={() => navigate('/dashboard/book-creator/rendering-studio')}
          >
            <Icon name="microphone" /> Go to the Rendering Studio
          </Button>
          <Button basic size="small" onClick={() => setPublishDone(false)}>
            Stay here
          </Button>
        </Message>
      )}

      {/* Step content */}
      {renderStepContent()}
      {/* Navigation is via the step bar above — no Previous/Next. */}

      {/* Cast merge/dismiss tooling (published series only) */}
      {isPublished && (
        <ManageCastModal
          open={castOpen}
          litGroupUuid={activePrototype.publishedLitGroupUuid}
          litGroupName={activePrototype.name}
          onClose={() => setCastOpen(false)}
        />
      )}
    </div>
  );
};

export default PrototypeWizard;
